import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { auth } from '@/lib/auth'
import PlanCards from '@/components/PlanCards'
import TopUpPacks from '@/components/TopUpPacks'
import CancelSubscriptionModal from '@/components/CancelSubscriptionModal'
import { clearPendingPlan, isPaidPlanId, notifyTierChanged } from '@/lib/billingPlans'
import { formatBillingDate } from '@/lib/billingDates'
import { supabase } from '@/lib/supabase'

const API_BASE =
  (import.meta as any).env?.VITE_API_URL || "https://api.linguaproz.com"

interface BillingInfo {
  tier: string
  subscription_status: string
  next_billing_date: string | null
  next_billing_at?: string | null
  cancel_at?: string | null
}

interface CatInvoice {
  id: string
  invoice_number: string
  created_at: string
  type: 'subscription' | 'pack'
  tier_or_pack: string
  amount_zar: number
}

const PACK_NAMES: Record<string, string> = { starter: 'Starter pack', standard: 'Standard pack' }

// "Standard plan" / "Premium plan" for subscriptions, "Starter pack" / "Standard pack" for packs.
function invoiceLabel(inv: CatInvoice): string {
  const key = (inv.tier_or_pack || '').toLowerCase()
  if (inv.type === 'pack') return PACK_NAMES[key] || `${key.charAt(0).toUpperCase()}${key.slice(1)} pack`
  return `${key.charAt(0).toUpperCase()}${key.slice(1)} plan`
}

function statusLabel(status: string | undefined, endsOn: string | null): string {
  if (status === 'cancelled' && endsOn) return `Cancelled, ends ${endsOn}`
  const s = status || 'none'
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export default function BillingPage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const payfastReturn = searchParams.get('payfast')
  const planParam = searchParams.get('plan')
  const highlightPlan = isPaidPlanId(planParam) ? planParam : null
  const [billing, setBilling] = useState<BillingInfo | null>(null)
  const [invoices, setInvoices] = useState<CatInvoice[]>([])
  const [billingLoading, setBillingLoading] = useState(true)
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [showCancel, setShowCancel] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [extraChars, setExtraChars] = useState<number | null>(null)

  // The chosen plan has arrived here, so the stored intent is no longer needed.
  useEffect(() => {
    if (highlightPlan) clearPendingPlan()
  }, [highlightPlan])

  // Reads the plan and invoices. Returns what it read so callers can compare.
  const loadBilling = async (): Promise<{ billing: BillingInfo | null; invoices: CatInvoice[] }> => {
    const session = await auth.getSession()
    if (!session?.access_token) return { billing: null, invoices: [] }
    const headers = { Authorization: `Bearer ${session.access_token}` }
    const [billingRes, invoicesRes] = await Promise.all([
      fetch(`${API_BASE}/api/cat/payfast/billing`, { headers }),
      fetch(`${API_BASE}/api/cat/payfast/invoices`, { headers }),
    ])
    const nextBilling: BillingInfo | null = billingRes.ok ? await billingRes.json() : null
    const nextInvoices: CatInvoice[] = invoicesRes.ok ? await invoicesRes.json() : []
    if (nextBilling) setBilling(nextBilling)
    if (invoicesRes.ok) setInvoices(nextInvoices)

    // Pack credit lives in public.user_usage.mt_chars_extra (one row per user).
    if (user?.id) {
      const { data: usage } = await supabase
        .from('user_usage')
        .select('mt_chars_extra')
        .eq('user_id', user.id)
        .maybeSingle()
      setExtraChars(usage?.mt_chars_extra ?? 0)
    }
    return { billing: nextBilling, invoices: nextInvoices }
  }

  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    ;(async () => {
      try {
        await loadBilling()
      } catch (err) {
        console.error('Failed to load billing info', err)
      } finally {
        if (!cancelled) setBillingLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [user?.id])

  // Back from PayFast: the payment notice can arrive a few seconds after the
  // customer does, so look again until a new invoice appears.
  useEffect(() => {
    if (!user?.id || !payfastReturn) return
    let stopped = false
    const done = (message: string | null) => {
      if (stopped) return
      if (message) setNotice(message)
      const next = new URLSearchParams(searchParams)
      next.delete('payfast')
      setSearchParams(next, { replace: true })
    }
    if (payfastReturn === 'cancelled') {
      done('Checkout was cancelled. You have not been charged.')
      return
    }
    if (payfastReturn !== 'success') {
      done(null)
      return
    }
    ;(async () => {
      try {
        const start = await loadBilling()
        const startCount = start.invoices.length
        for (let attempt = 0; attempt < 10 && !stopped; attempt++) {
          const now = await loadBilling()
          if (now.invoices.length > startCount) {
            notifyTierChanged()
            done('Payment received. Thank you. Your plan is up to date.')
            return
          }
          await new Promise(resolve => setTimeout(resolve, 3000))
        }
        notifyTierChanged()
        done('Your payment is being processed. Your plan will update shortly, and you can refresh this page in a minute.')
      } catch (err) {
        console.error('Failed to confirm payment', err)
        done(null)
      }
    })()
    return () => { stopped = true }
  }, [user?.id, payfastReturn])

  const handleConfirmCancel = async (reason: string, details: string) => {
    setCancelling(true)
    setCancelError(null)
    try {
      const session = await auth.getSession()
      if (!session?.access_token) throw new Error('Please sign in again to continue.')
      const res = await fetch(`${API_BASE}/api/cat/payfast/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason, details: details.trim() || undefined }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to cancel subscription.')
      setShowCancel(false)
      await loadBilling()
      notifyTierChanged()
    } catch (err: any) {
      setCancelError(err.message || 'Failed to cancel subscription.')
    } finally {
      setCancelling(false)
    }
  }

  const handleDownloadInvoice = async (invoiceId: string) => {
    setDownloadingId(invoiceId)
    try {
      const session = await auth.getSession()
      if (!session?.access_token) throw new Error('Please sign in again to continue.')
      const res = await fetch(`${API_BASE}/api/cat/payfast/invoices/${invoiceId}/download`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      const data = await res.json()
      if (!res.ok || !data.url) throw new Error(data.error || 'Failed to generate download link.')
      window.open(data.url, '_blank')
    } catch (err: any) {
      console.error(err)
      alert(err.message || 'Failed to download invoice.')
    } finally {
      setDownloadingId(null)
    }
  }

  const planName = (billing?.tier || 'free').charAt(0).toUpperCase() + (billing?.tier || 'free').slice(1)
  const endsOn = billing?.cancel_at && new Date(billing.cancel_at) > new Date()
    ? formatBillingDate(billing.cancel_at)
    : null
  const cancelDialogEnd = formatBillingDate(billing?.next_billing_at || billing?.next_billing_date) || null

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Plan &amp; billing</h1>
        <p className="text-gray-700 text-sm mt-1">Your TranslyxCAT plan, subscription and invoices</p>
      </div>
      <div className="space-y-4">
        {notice && (
          <div role="status" className="flex items-start justify-between gap-3 rounded-xl border border-gold-500/40 bg-gold-50 px-4 py-3 text-sm text-slate-900">
            <span>{notice}</span>
            <button onClick={() => setNotice(null)} className="text-gray-500 hover:text-gray-800" aria-label="Dismiss">×</button>
          </div>
        )}
        {billing?.subscription_status === 'cancelled' && endsOn && (
          <div role="status" className="rounded-xl border border-gold-500/40 bg-gold-50 px-4 py-3 text-sm text-slate-900">
            Your {planName} plan ends on {endsOn}. You can subscribe again at any time after that.
          </div>
        )}
        {billingLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <p className="text-sm text-gray-500">Loading your plan…</p>
          </div>
        ) : !billing ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <p className="text-sm text-gray-700">We could not load your plan. Please refresh the page and try again.</p>
          </div>
        ) : (
          <PlanCards
            currentTier={billing.tier || 'free'}
            hasActiveSubscription={billing.tier !== 'free' && billing.subscription_status === 'active'}
            highlightPlan={highlightPlan}
          />
        )}
        <TopUpPacks extraChars={extraChars} hasBoughtPack={invoices.some(inv => inv.type === 'pack')} />
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Billing details</h2>
          <p className="text-sm text-gray-800 mb-4">Your TranslyxCAT subscription and invoices</p>

          {billingLoading ? (
            <p className="text-sm text-gray-500">Loading billing info…</p>
          ) : (
            <>
              <div className="space-y-3 mb-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Current tier</span>
                  <span className="text-sm font-semibold text-gray-900 capitalize">{billing?.tier || 'free'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Subscription status</span>
                  <span className="text-sm text-gray-700">
                    {statusLabel(billing?.subscription_status, endsOn)}
                  </span>
                </div>
                {billing?.next_billing_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Next billing date</span>
                    <span className="text-sm text-gray-700">
                      {formatBillingDate(billing.next_billing_date)}
                    </span>
                  </div>
                )}
              </div>

              {billing && billing.tier !== 'free' && billing.subscription_status === 'active' && (
                <div className="mb-5">
                  <button onClick={() => { setCancelError(null); setShowCancel(true) }}
                    className="px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium transition-colors">
                    Cancel subscription
                  </button>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Invoice history</h3>
                {invoices.length === 0 ? (
                  <p className="text-sm text-gray-500">No invoices yet.</p>
                ) : (
                  <div className="space-y-2">
                    {invoices.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{inv.invoice_number}</div>
                          <div className="text-xs text-gray-500">
                            {formatBillingDate(inv.created_at)} · {invoiceLabel(inv)}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-gray-900">R {Number(inv.amount_zar).toFixed(2)}</span>
                          <button onClick={() => handleDownloadInvoice(inv.id)} disabled={downloadingId === inv.id}
                            className="text-sm text-gold-600 hover:text-gold-500 font-medium disabled:opacity-60">
                            {downloadingId === inv.id ? '...' : 'Download'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {showCancel && billing && (
        <CancelSubscriptionModal
          planName={planName}
          endsOn={cancelDialogEnd}
          submitting={cancelling}
          error={cancelError}
          onKeep={() => setShowCancel(false)}
          onConfirm={handleConfirmCancel}
        />
      )}
    </div>
  )
}
